<template>
  <!-- Filters section -->
  <div class="grid m-0 w-full">
    <div
      v-for="(filtro, index) in filtros"
      :key="index"
      :class="section == 'Top' ? 'col-12 md:col-6 lg:col-3' : 'col-12'"
    >
      <!-- Valor único / Valor de rango -->
      <div
        v-if="isValorUnico(filtro) || isValorDeRango(filtro)"
        class="flex gap-2"
      >
        <!-- Texto -->
        <div v-if="isTexto(filtro)" class="flex flex-column gap-1 w-full">
          <label class="custom-label">{{ filtro.titulo }}</label>
          <InputText
            v-model="values[index]"
            @update:modelValue="valuesUpdated()"
            class="w-full"
          />
        </div>

        <!-- Entero / Decimal -->
        <template v-if="isEntero(filtro) || isDecimal(filtro)">
          <div class="flex flex-column gap-1 w-full">
            <label class="custom-label">{{ filtro.titulo }}</label>
            <InputNumber
              v-model="values[index]"
              @update:modelValue="valuesUpdated()"
              class="custom-input-number w-full"
              :maxFractionDigits="isDecimal(filtro) ? 5 : 0"
              :min="getMinValue(filtro)"
              :max="getMaxValue(filtro)"
              :placeholder="isValorDeRango(filtro) ? 'Mín.' : ''"
            />
          </div>

          <span v-if="isValorDeRango(filtro)" class="range-separator">-</span>

          <!-- Con rango -->
          <div
            v-if="isValorDeRango(filtro)"
            class="flex flex-column gap-1 w-full"
          >
            <label class="custom-label invisible-label">{{ filtro.titulo }}</label>
            <InputNumber
              v-model="ranges[index]"
              @update:modelValue="valuesUpdated()"
              class="custom-input-number w-full"
              :placeholder="'Máx.'"
              :maxFractionDigits="isDecimal(filtro) ? 5 : 0"
              :min="getMinValue(filtro)"
              :max="getMaxValue(filtro)"
            />
          </div>
        </template>

        <!-- Fecha -->
        <template v-if="isFecha(filtro)">
          <div class="flex flex-column gap-1 w-full">
            <label class="custom-label">{{ filtro.titulo }}</label>
            <Calendar
              v-model="values[index]"
              @update:modelValue="valuesUpdated()"
              :placeholder="isValorDeRango(filtro) ? 'Desde' : ''"
              class="w-full"
              :manualInput="false"
              :showButtonBar="true"
            />
          </div>

          <span v-if="isValorDeRango(filtro)" class="range-separator">-</span>

          <!-- Con rango -->
          <div
            v-if="isValorDeRango(filtro)"
            class="flex flex-column gap-1 w-full"
          >
            <label class="custom-label invisible-label">{{ filtro.titulo }}</label>
            <Calendar
              v-model="ranges[index]"
              @update:modelValue="valuesUpdated()"
              class="w-full"
              :placeholder="'Hasta'"
              :manualInput="false"
              :showButtonBar="true"
            />
          </div>
        </template>
      </div>

      <!-- Lista de valores -->
      <div v-if="isListaDeValores(filtro)">
        <!-- Sin selector múltiple -->
        <div
          v-if="!tieneSeleccionMultiple(filtro)"
          class="flex flex-column gap-1 w-full"
        >
          <label class="custom-label">{{ filtro.titulo }}</label>
          <Dropdown
            v-model="values[index]"
            @update:modelValue="valuesUpdated()"
            class="w-full"
            :options="filtro.claseFiltroDto.valores"
            optionLabel="clave"
            optionValue="valor"
            filter
            showClear
          />
        </div>

        <!-- Con selector múltiple -->
        <div
          v-if="tieneSeleccionMultiple(filtro)"
          class="flex flex-column gap-1 w-full"
        >
          <label class="custom-label">{{ filtro.titulo }}</label>
          <MultiSelect
            v-model="values[index]"
            @update:modelValue="valuesUpdated()"
            class="w-full"
            :options="filtro.claseFiltroDto.valores"
            optionLabel="clave"
            optionValue="valor"
            filter
            showClear
            display="chip"
          />
        </div>
      </div>

      <!-- Error en el filtro -->
      <small v-if="errors[index]" class="text-red-400">{{
        errors[index]
      }}</small>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Calendar from "primevue/calendar";
import Dropdown from "primevue/dropdown";
import MultiSelect from "primevue/multiselect";

// #region Props, Emits
const props = defineProps({
  filtros: {
    type: Array,
    required: true,
  },
  section: {
    type: String,
    required: true,
  },
});
// #endregion

// #region  Params, Ref, Reactives, Computed
const values = ref([]);
const ranges = ref([]);
const errors = ref([]);
// #endregion

// #region Functions
function getTipo(filtro) {
  return filtro.tipoFiltroDto.nombreTipoFiltro;
}

function isTexto(filtro) {
  return getTipo(filtro) == "texto";
}

function isEntero(filtro) {
  return getTipo(filtro) == "entero";
}

function isDecimal(filtro) {
  return getTipo(filtro) == "decimal";
}

function isFecha(filtro) {
  return getTipo(filtro) == "fecha";
}

function getClase(filtro) {
  return filtro.claseFiltroDto.nombreClaseFiltro;
}

function isValorDeRango(filtro) {
  return getClase(filtro) == "Valor de rango";
}

function isValorUnico(filtro) {
  return getClase(filtro) == "Valor único";
}

function isListaDeValores(filtro) {
  return getClase(filtro) == "Lista de valores";
}

function valuesUpdated() {
  validateValues(values.value);
  validateValues(ranges.value);
}

function validateValues(values) {
  values.forEach((value, index) => {
    if (value) {
      const filtro = props.filtros[index];
      let filtroRegex = filtro.claseFiltroDto.valorRegex;

      if (filtroRegex) {
        filtroRegex = new RegExp(filtroRegex);
        errors.value[index] = filtroRegex.test(value) ? "" : "Valor inválido";
      }
    } else {
      errors.value[index] = "";
    }
  });
}

function getMinValue(filtro) {
  return filtro.claseFiltroDto.valorMin && isValorDeRango(filtro)
    ? filtro.claseFiltroDto.valorMin
    : null;
}

function getMaxValue(filtro) {
  return filtro.claseFiltroDto.valorMax && isValorDeRango(filtro)
    ? filtro.claseFiltroDto.valorMax
    : null;
}

function tieneSeleccionMultiple(filtro) {
  return filtro.claseFiltroDto.tieneSeleccionMultiple;
}

function convertToDate(value, tipo) {
  if (tipo == "fecha") {
    return new Date(value);
  }

  return value;
}
// #endregion

// #region Effects, Hooks
onMounted(() => {
  props.filtros.forEach((filtro) => {
    const claseFiltroDto = filtro.claseFiltroDto;
    const tipoFiltroDto = filtro.tipoFiltroDto;

    if (claseFiltroDto.nombreClaseFiltro == "Valor único") {
      values.value.push(
        convertToDate(
          filtro.valorPredeterminado[0],
          tipoFiltroDto.nombreTipoFiltro
        )
      );
    }

    if (claseFiltroDto.nombreClaseFiltro == "Valor de rango") {
      values.value.push(
        convertToDate(
          filtro.valorPredeterminado[0],
          tipoFiltroDto.nombreTipoFiltro
        )
      );
      ranges.value.push(
        convertToDate(
          filtro.valorPredeterminado[1],
          tipoFiltroDto.nombreTipoFiltro
        )
      );
    } else {
      // Para mantener la sincronización de índices con values
      ranges.value.push(null);
    }

    if (claseFiltroDto.nombreClaseFiltro == "Lista de valores") {
      if (claseFiltroDto.tieneSeleccionMultiple) {
        if (tipoFiltroDto.nombreTipoFiltro == "fecha") {
          values.value.push(filtro.valorPredeterminado.map((v) => new Date(v)));
        } else {
          values.value.push(filtro.valorPredeterminado);
        }
      } else {
        values.value.push(
          convertToDate(
            filtro.valorPredeterminado[0],
            tipoFiltroDto.nombreTipoFiltro
          )
        );
      }
    }

    if (tipoFiltroDto.nombreTipoFiltro == "fecha") {
      filtro.claseFiltroDto.valores.forEach((valor, index2) => {
        filtro.claseFiltroDto.valores[index2].valor = new Date(valor.valor);
      });
    }
  });
});
// #endregion

// #region Provide, Expose
defineExpose({
  values,
  ranges,
});
// #endregion
</script>

<style lang="scss" scoped>
.custom-label {
  color: #1e1e1e;
  font-weight: bold;
  font-size: 0.9rem;
}

.custom-input-number {
  :deep(.p-inputtext) {
    width: 100%;
  }
}

.invisible-label {
  visibility: hidden;
}

.range-separator {
  align-self: flex-end;
  padding-bottom: 0.85rem;
  color: #6b7280;
  font-size: 1rem;
  user-select: none;
}

</style>
